/**
 * Bava SPA Navigation — keeps the header (and background music) alive across page changes
 * by fetching new pages via XHR and swapping only #MainContent.
 */
(function () {
  'use strict';

  // Don't run in Shopify theme editor or unsupported browsers
  if (window.Shopify && window.Shopify.designMode) return;
  if (!window.history || !window.fetch || !window.DOMParser) return;

  var MAIN_ID = 'MainContent';
  var navigating = false;

  function parseUrl(href) {
    try { return new URL(href, location.origin); } catch (e) { return null; }
  }

  // --- Scroll memory ---------------------------------------------------------
  // Because we swap #MainContent instead of loading a document, the browser can
  // no longer restore the scroll position on Back/Forward. We keep our own store
  // keyed by history entry: every entry we create gets a `bavaKey`, and the
  // position for the visible entry is kept up to date while the visitor scrolls.

  var SCROLL_STORE_KEY = 'bava:scrollPositions';
  var SCROLL_SEQ_KEY = 'bava:scrollSeq';
  var MAX_STORED_POSITIONS = 50;

  // How long to let the swapped-in page grow before starting the glide down
  var SETTLE_MS = 600;
  var GLIDE_MIN_MS = 450;
  var GLIDE_MAX_MS = 1100;
  var GLIDE_MS_PER_PX = 0.4;
  var MAX_GLIDE_EXTENSIONS = 4;
  // Longest per-frame step the animation will credit. The SPA swap (innerHTML +
  // runScripts + observer setup) can block the main thread long enough to drop a
  // second of frames; without this clamp the next frame computes t >= 1 and the
  // whole travel collapses into a single jump.
  var MAX_FRAME_DELTA_MS = 50;
  // Window after a restore starts during which scroll gestures don't cancel it
  var ABORT_GRACE_MS = 250;
  // Give up and hand scrolling back if a restore somehow never completes
  var RESTORE_SAFETY_MS = 6000;

  var scrollPositions = {};
  var currentKey = null;
  var trackScroll = true;
  var restoreTimers = [];
  var restoreFrame = null;
  // Cleanup for the restore currently in flight, so a second navigation can
  // stop it rather than leaving two of them fighting over the scroll position
  var abortRestore = null;

  try {
    scrollPositions = JSON.parse(sessionStorage.getItem(SCROLL_STORE_KEY)) || {};
  } catch (e) {
    scrollPositions = {};
  }

  function persistScrollPositions() {
    try {
      var keys = Object.keys(scrollPositions);
      // Keep the store bounded — drop the oldest entries first
      if (keys.length > MAX_STORED_POSITIONS) {
        keys.slice(0, keys.length - MAX_STORED_POSITIONS).forEach(function (k) {
          delete scrollPositions[k];
        });
      }
      sessionStorage.setItem(SCROLL_STORE_KEY, JSON.stringify(scrollPositions));
    } catch (e) {
      // private mode / quota exceeded — scroll memory is best-effort
    }
  }

  function nextHistoryKey() {
    var seq = 0;
    try { seq = parseInt(sessionStorage.getItem(SCROLL_SEQ_KEY), 10) || 0; } catch (e) {}
    seq += 1;
    try { sessionStorage.setItem(SCROLL_SEQ_KEY, String(seq)); } catch (e) {}
    return 'b' + seq;
  }

  function historyKey() {
    return (history.state && history.state.bavaKey) || null;
  }

  function rememberScroll() {
    if (!trackScroll || !currentKey) return;
    scrollPositions[currentKey] = window.scrollY || window.pageYOffset || 0;
  }

  function cancelScrollRestore() {
    restoreTimers.forEach(clearTimeout);
    restoreTimers = [];
    if (restoreFrame) {
      cancelAnimationFrame(restoreFrame);
      restoreFrame = null;
    }
  }

  function nowMs() {
    return window.performance && performance.now ? performance.now() : +new Date();
  }

  function currentScroll() {
    return window.scrollY || window.pageYOffset || 0;
  }

  function maxScrollTop() {
    var height = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
    return Math.max(0, height - window.innerHeight);
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Coming Back, we open the page at the top and then travel down to the section
  // the visitor left from, rather than teleporting there. Landing mid-page with
  // no motion costs them all sense of where they are.
  function restoreScrollTo(target) {
    if (abortRestore) abortRestore();
    cancelScrollRestore();
    trackScroll = false;

    var done = false;

    function finish() {
      if (done) return;
      done = true;
      if (abortRestore === finish) abortRestore = null;
      cancelScrollRestore();
      window.removeEventListener('wheel', onUserTakeover);
      window.removeEventListener('touchmove', onUserTakeover);
      window.removeEventListener('keydown', onUserTakeover);
      trackScroll = true;
    }

    // Only a deliberate scroll gesture cancels the travel. `touchmove` rather
    // than `touchstart`, because a plain tap must not stop it, and a short grace
    // period so trailing momentum from before the click doesn't either.
    var acceptAbortAfter = 0;

    function onUserTakeover() {
      if (nowMs() < acceptAbortAfter) return;
      finish();
    }

    abortRestore = finish;

    // Every restored page opens at the top
    window.scrollTo(0, 0);

    // Forward navigation (or nothing worth returning to) — we're already there
    if (!target) {
      finish();
      return;
    }

    acceptAbortAfter = nowMs() + ABORT_GRACE_MS;
    window.addEventListener('wheel', onUserTakeover, { passive: true });
    window.addEventListener('touchmove', onUserTakeover, { passive: true });
    window.addEventListener('keydown', onUserTakeover);
    restoreTimers.push(setTimeout(finish, RESTORE_SAFETY_MS));

    // Respect a reduced-motion preference: put them back without the travel
    if (prefersReducedMotion()) {
      restoreTimers.push(
        setTimeout(function () {
          if (done) return;
          window.scrollTo(0, Math.min(target, maxScrollTop()));
          finish();
        }, 80)
      );
      return;
    }

    var from = 0;
    var duration = 0;
    var elapsed = 0;
    var lastFrame = null;
    var extensions = 0;
    var settleStart = null;
    var settleLast = null;

    // Phase 1 — hold at the top until the swapped-in content is tall enough to
    // hold `target` AND frames are actually flowing again. Starting mid-stall is
    // what made the travel collapse into a jump; starting too early leaves it
    // stopping short, because lazy images keep the page shorter than `target`.
    function settle(now) {
      if (done) return;
      if (settleStart === null) settleStart = now;

      var framesFlowing = settleLast !== null && now - settleLast < MAX_FRAME_DELTA_MS;
      settleLast = now;

      if ((maxScrollTop() >= target && framesFlowing) || now - settleStart >= SETTLE_MS) {
        startGlide();
        return;
      }
      restoreFrame = requestAnimationFrame(settle);
    }

    // Phase 2 — ease down to the section.
    function startGlide() {
      from = currentScroll();
      elapsed = 0;
      lastFrame = null;
      // Longer trips glide for longer, bounded so a deep page never drags
      duration = Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, Math.abs(target - from) * GLIDE_MS_PER_PX));
      restoreFrame = requestAnimationFrame(stepGlide);
    }

    function stepGlide(now) {
      if (done) return;
      if (lastFrame === null) lastFrame = now;

      // Advance on clamped per-frame deltas rather than wall-clock elapsed time,
      // so a blocked main thread stretches the travel instead of skipping it
      elapsed += Math.min(now - lastFrame, MAX_FRAME_DELTA_MS);
      lastFrame = now;

      var t = Math.min(1, elapsed / duration);
      // Re-clamp each frame — the page is still growing underneath us
      var goal = Math.min(target, maxScrollTop());
      window.scrollTo(0, Math.round(from + (goal - from) * easeInOutCubic(t)));

      if (t < 1) {
        restoreFrame = requestAnimationFrame(stepGlide);
        return;
      }

      // The page grew while we were travelling, so we stopped short — carry on
      var position = currentScroll();
      if (extensions < MAX_GLIDE_EXTENSIONS && target - position > 4 && maxScrollTop() > position + 4) {
        extensions++;
        startGlide();
        return;
      }
      finish();
    }

    restoreFrame = requestAnimationFrame(settle);
  }

  // A full page load (reload, or a link that fell through to a real navigation)
  // lands at the top now that native restoration is off, so put the visitor back
  // ourselves. Instant rather than a glide — a reload should feel like a reload.
  function restoreOnLoad() {
    var target = scrollPositions[currentKey] || 0;
    // Nothing to do, or the browser already positioned us (e.g. an #anchor)
    if (!target || currentScroll() > 0) return;

    var attempts = 0;
    trackScroll = false;

    function stop() {
      if (attempts === Infinity) return;
      attempts = Infinity;
      if (abortRestore === stop) abortRestore = null;
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchmove', stop);
      window.removeEventListener('keydown', stop);
      trackScroll = true;
    }

    abortRestore = stop;
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchmove', stop, { passive: true });
    window.addEventListener('keydown', stop);

    // Keep re-applying while images load and the page grows into the position
    function attempt() {
      if (attempts === Infinity) return;
      window.scrollTo(0, Math.min(target, maxScrollTop()));
      attempts++;
      if (attempts < 15 && Math.abs(currentScroll() - target) > 2) {
        restoreTimers.push(setTimeout(attempt, 100));
        return;
      }
      stop();
    }

    attempt();
  }

  var scrollTicking = false;
  window.addEventListener('scroll', function () {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(function () {
      scrollTicking = false;
      rememberScroll();
    });
  }, { passive: true });

  window.addEventListener('pagehide', function () {
    rememberScroll();
    persistScrollPositions();
  });

  // Every host known to serve this storefront, compared with `www.` stripped:
  // the one we're on, plus the shop's primary and permanent (.myshopify.com)
  // domains injected by theme.liquid.
  var STORE_HOSTS = (function () {
    var declared = window.__bavaStoreHosts || [];
    return [location.hostname]
      .concat(Array.prototype.slice.call(declared))
      .filter(Boolean)
      .map(function (host) { return String(host).toLowerCase().replace(/^www\./, ''); });
  })();

  // Theme content is regularly authored with an absolute URL pointing at one of
  // the store's other domains. That's the same storefront, so rewrite it onto
  // the origin we're actually browsing — left alone it reads as cross-origin,
  // the fetch would be blocked, and the link falls through to a full page load
  // (which skips scroll restoration entirely). Returns null for genuinely
  // external links.
  function toCurrentOrigin(url) {
    if (!url) return null;
    if (url.origin === location.origin) return url;
    var host = url.hostname.toLowerCase().replace(/^www\./, '');
    if (STORE_HOSTS.indexOf(host) === -1) return null;
    return parseUrl(url.pathname + url.search + url.hash);
  }

  function shouldIntercept(url) {
    if (!url) return false;
    // Must be same origin
    if (url.origin !== location.origin) return false;
    var p = url.pathname;
    // Skip checkout, payments, admin
    if (/^\/(checkout|payments|admin)/.test(p)) return false;
    // Skip file downloads
    if (/\.(pdf|zip|docx?|xlsx?|png|jpe?g|gif|svg|webp|mp3|mp4|mov|avi|wmv)$/i.test(p)) return false;
    // Skip hash-only changes (same path + query)
    if (url.pathname === location.pathname && url.search === location.search) return false;
    return true;
  }

  // Inject any new stylesheets the fetched page needs that aren't already loaded
  function syncStylesheets(newDoc) {
    var existing = new Set(
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(function (l) { return l.href; })
    );
    newDoc.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
      if (link.href && !existing.has(link.href)) {
        var el = document.createElement('link');
        el.rel = 'stylesheet';
        el.href = link.href;
        document.head.appendChild(el);
      }
    });
  }

  // Re-execute inline <script> tags so section initializers run on new content.
  // External scripts (src=) that are already in the page are skipped — they executed
  // once on the initial load, their side-effects (custom element definitions, global
  // vars) persist, and re-inserting them triggers async crashes outside any try/catch.
  var SKIP_SRC_PATTERN = /cdn\.shopify\.com\/s\/files|shopifycdn\.com|cdn\.smartsizeme|sizefox|cloudfront\.net\/smartsize/i;

  // Seed the set with every <script src> already present in the document so that
  // scripts loaded by the initial page render are never re-executed.
  var _loadedSrcs = new Set(
    Array.from(document.querySelectorAll('script[src]')).map(function (s) { return s.src; })
  );

  function runScripts(container) {
    container.querySelectorAll('script').forEach(function (old) {
      // Skip external scripts from known third-party CDNs
      if (old.src && SKIP_SRC_PATTERN.test(old.src)) return;
      // Skip inline scripts injected by SmartSize / size-chart apps
      if (!old.src && old.textContent && /SmartSize|sizefox|smartsize/i.test(old.textContent)) return;
      // Skip inline scripts that (re-)define custom elements — already registered globally
      if (!old.src && old.textContent && /customElements\.define/.test(old.textContent)) return;
      // Skip any external script already loaded in this session to avoid async re-run crashes
      if (old.src && _loadedSrcs.has(old.src)) return;

      // Guard: node may have been detached by a previously-executed script
      if (!old.parentNode) return;

      var neo = document.createElement('script');
      Array.from(old.attributes).forEach(function (a) { neo.setAttribute(a.name, a.value); });
      if (!old.src) neo.textContent = old.textContent;
      if (old.src) _loadedSrcs.add(old.src);
      try {
        old.parentNode.replaceChild(neo, old);
      } catch (e) {
        // script threw during SPA swap — ignore and continue
      }
    });
  }

  // Close the mobile/tablet nav drawer and any open desktop mega-menu (Shop,
  // Collections, etc). Both live outside #MainContent, so nothing else closes
  // them when a click gets hijacked for an SPA navigation.
  function closeHeaderMenus() {
    try {
      document.querySelectorAll('header-menu details[open]').forEach(function (details) {
        details.removeAttribute('open');
        var summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      });

      var mainToggle = document.querySelector('header-drawer #Details-menu-drawer-container');
      if (!mainToggle) return;

      // Run unconditionally rather than only when the drawer is currently open, so a
      // stale aria-expanded left behind by an earlier navigation gets cleared too.
      mainToggle.removeAttribute('open');
      mainToggle.classList.remove('menu-opening');
      mainToggle.querySelectorAll('details').forEach(function (d) {
        d.removeAttribute('open');
        d.classList.remove('menu-opening');
      });
      mainToggle.querySelectorAll('.submenu-open').forEach(function (s) {
        s.classList.remove('submenu-open');
      });

      // Put every summary back to aria-expanded="false". Dawn only resets this from
      // its own summary click handler, which an SPA navigation bypasses, so without
      // this the drawer keeps announcing itself as expanded to screen readers.
      // (The invisible tap-catching backdrop used to hang off this attribute too;
      // it is now keyed to details[open].menu-opening in base.css, so a stale value
      // here no longer leaves a layer covering the page we navigate to.)
      mainToggle.querySelectorAll('summary').forEach(function (s) {
        s.setAttribute('aria-expanded', 'false');
      });

      var header = document.querySelector('.section-header');
      if (header) header.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden-desktop', 'overflow-hidden-tablet', 'overflow-hidden-mobile');
    } catch (e) {
      // never block navigation over menu cleanup
    }
  }

  function swapPage(newDoc, resolvedUrl, pushState) {
    var newMain = newDoc.getElementById(MAIN_ID);
    var curMain = document.getElementById(MAIN_ID);

    // Safety: if either element is missing, do a full navigation
    if (!newMain || !curMain) {
      location.href = resolvedUrl;
      return;
    }

    // Belt-and-braces: also close the nav drawer/mega-menu here in case
    // something reopened it during the fetch (see closeHeaderMenus at click time).
    closeHeaderMenus();

    // Close cart drawer without CSS animation (setting transition:none prevents
    // transitionend from firing, which avoids the trapFocus-null error in open()'s
    // { once:true } transitionend callback)
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.style.transition = 'none';
      cartDrawer.classList.remove('active', 'animate');
      document.body.classList.remove('overflow-hidden');
      requestAnimationFrame(function () { cartDrawer.style.removeProperty('transition'); });
    }

    // Load any missing stylesheets before swapping content
    syncStylesheets(newDoc);

    // Update page title and meta
    document.title = newDoc.title;

    var newDesc = newDoc.querySelector('meta[name="description"]');
    var curDesc = document.querySelector('meta[name="description"]');
    if (newDesc) {
      if (curDesc) curDesc.setAttribute('content', newDesc.getAttribute('content') || '');
      else {
        var d = document.createElement('meta');
        d.name = 'description';
        d.content = newDesc.getAttribute('content') || '';
        document.head.appendChild(d);
      }
    }

    var newCanon = newDoc.querySelector('link[rel="canonical"]');
    var curCanon = document.querySelector('link[rel="canonical"]');
    if (newCanon && curCanon) curCanon.href = newCanon.href;

    // Keep body class in sync (template-specific classes may differ)
    document.body.className = newDoc.body.className;

    // Swap main content
    curMain.innerHTML = newMain.innerHTML;
    curMain.className = newMain.className;

    // Re-run inline scripts inside the new content
    try {
      runScripts(curMain);
    } catch (e) {
      // Unexpected runScripts failure — log but don't block the rest of the swap
      // (animation init below must always run or content stays invisible)
    }

    // Reinitialise scroll animations for the new content (animations.js only
    // runs on DOMContentLoaded so we must call it manually after each SPA swap)
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
    if (typeof initializeScrollZoomAnimationTrigger === 'function') {
      initializeScrollZoomAnimationTrigger();
    }

    // Push URL to browser history
    if (pushState) {
      currentKey = nextHistoryKey();
      history.pushState({ bavaSPA: true, href: resolvedUrl, bavaKey: currentKey }, newDoc.title, resolvedUrl);
      persistScrollPositions();
    } else {
      // Back/Forward — this entry already carries the key we filed it under
      currentKey = historyKey() || currentKey;
    }

    // A new page starts at the top; Back/Forward returns to where we left off
    restoreScrollTo(pushState ? 0 : scrollPositions[currentKey] || 0);

    // Restore focus to main for accessibility — preventScroll so this never
    // undoes the position we just restored
    curMain.setAttribute('tabindex', '-1');
    curMain.focus({ preventScroll: true });

    // Signal to any other scripts that a navigation occurred
    document.dispatchEvent(new CustomEvent('bava:navigated', {
      bubbles: true,
      detail: { url: resolvedUrl }
    }));
  }

  function navigateTo(url, pushState) {
    if (navigating) return;
    navigating = true;

    // Stop any glide still in flight before it fights the new navigation. This
    // re-enables tracking, so the position filed below is wherever the glide had
    // reached when the visitor clicked away.
    if (abortRestore) abortRestore();

    // File the position of the entry we're leaving so Back can return to it.
    // Skipped on a popstate: the browser may already have moved us by then, and
    // the scroll tracker has kept that entry's position current anyway.
    if (pushState) rememberScroll();
    // Freeze tracking for the duration of the swap — replacing #MainContent
    // shortens the document and the resulting clamp would overwrite the value.
    trackScroll = false;
    persistScrollPositions();

    fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'X-Bava-SPA': '1' }
    })
      .then(function (res) {
        // If the server redirected us off-site (e.g. Shopify checkout), follow normally
        if (res.url) {
          var resUrl = parseUrl(res.url);
          if (resUrl && resUrl.origin !== location.origin) {
            location.href = res.url;
            return null;
          }
        }

        if (!res.ok) {
          location.href = url;
          return null;
        }

        return res.text().then(function (html) {
          return { html: html, finalUrl: res.url || url };
        });
      })
      .then(function (result) {
        if (!result) return;
        var doc = new DOMParser().parseFromString(result.html, 'text/html');
        swapPage(doc, result.finalUrl, pushState);
      })
      .catch(function () {
        location.href = url;
      })
      .finally(function () {
        navigating = false;
        // Deferred close: renderContents schedules open() via setTimeout(0).
        // Our setTimeout(0) queues AFTER that, so we close any drawer that
        // was re-opened by a renderContents race during the page fetch.
        setTimeout(function () {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && drawer.classList.contains('active')) {
            drawer.style.transition = 'none';
            drawer.classList.remove('active', 'animate');
            document.body.classList.remove('overflow-hidden');
            requestAnimationFrame(function () { drawer.style.removeProperty('transition'); });
          }
        }, 0);
      });
  }

  // --- Event listeners ---

  // Intercept link clicks
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // Walk up the DOM to find the <a> element
    var a = null;
    var node = e.target;
    while (node && node !== document) {
      if (node.tagName === 'A') { a = node; break; }
      node = node.parentElement;
    }
    if (!a) return;

    // Let the browser handle special link types
    if (a.target === '_blank' || a.target === '_parent' || a.target === '_top') return;
    if (a.hasAttribute('download')) return;
    if ((a.rel || '').indexOf('external') !== -1) return;
    if (a.dataset && a.dataset.noSpa !== undefined) return;
    // Skip links used by Shopify app/cart actions (href starts with #, javascript:, etc.)
    var href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#' || href.indexOf('javascript:') === 0) return;

    var url = toCurrentOrigin(parseUrl(a.href));
    if (!url || !shouldIntercept(url)) return;

    e.preventDefault();
    closeHeaderMenus();
    navigateTo(url.href, true);
  }, true);

  // Intercept GET form submissions (e.g. the /search form) so they don't reload the page.
  // Exclude facet filter forms — facets.js manages those itself via AJAX.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (e.defaultPrevented) return;
    if ((form.method || 'get').toLowerCase() !== 'get') return;
    // facets.js owns forms inside <facet-filters-form> and sort forms
    if (form.closest('facet-filters-form')) return;
    if (form.id === 'FacetSortForm' || form.classList.contains('facets-vertical-form')) return;
    if (form.dataset && form.dataset.noSpa !== undefined) return;

    var action = form.getAttribute('action') || location.href;
    var url = toCurrentOrigin(parseUrl(action));
    if (!url || !shouldIntercept(url)) return;

    // Build the query string from the form fields
    var params = new URLSearchParams(new FormData(form));
    url.search = params.toString();

    e.preventDefault();
    navigateTo(url.href, true);
  }, true);

  // Handle browser back / forward buttons.
  // facets.js pushes { searchParams } entries — let it handle those itself.
  // Everything else we SPA-navigate so music is never interrupted.
  window.addEventListener('popstate', function (e) {
    if (e.state && 'searchParams' in e.state) {
      // facets.js owns this entry — still follow the key so we don't keep
      // writing scroll positions against the entry we just left
      currentKey = historyKey() || currentKey;
      return;
    }
    navigateTo(location.href, false);
  });

  // Tag the initial page in history so popstate knows it's ours. Reuse the key
  // already on the entry when there is one (reload / bfcache) so its saved
  // scroll position stays reachable.
  currentKey = historyKey() || nextHistoryKey();
  history.replaceState({ bavaSPA: true, href: location.href, bavaKey: currentKey }, document.title, location.href);

  // We own scroll restoration now. Left on 'auto', the browser also restores a
  // position of its own on Back — applied to the outgoing page, before our fetch
  // lands — which reads as a jump immediately before the travel starts.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  restoreOnLoad();

  // Expose globally so other scripts (blog tag filter, etc.) can trigger SPA navigation
  // instead of using location.href which causes full reloads and kills the music.
  window.__bavaSPANavigate = function(url) { navigateTo(url, true); };

})();
